<template>
  <vs-popup
    :active.sync="visible"
    :button-close-hidden="hardBlock"
    :background-color-popup="'rgba(255,255,255,0.97)'"
    :title="hardBlock ? 'Two-factor authentication is now mandatory' : 'Enable two-factor authentication'"
    class="mandatory-2fa-popup"
  >
    <div class="p-2">
      <!-- STEP 0 — soft prompt (before deadline) -->
      <div v-if="step === 'prompt'">
        <vs-alert color="danger" icon-pack="feather" icon="icon-alert-triangle" class="mb-4" :active="true">
          <strong>Your XYLO account is at immediate risk.</strong><br/>
          The platform is under <strong>active attack</strong>. Stolen passwords are being used right now to break into accounts and exfiltrate confidential client data, prices, suppliers and orders. A single phishing email is enough to take over your account — and through it, the company's data.
        </vs-alert>
        <vs-alert color="warning" icon-pack="feather" icon="icon-clock" class="mb-4" :active="true">
          Two-factor authentication will be <strong>MANDATORY on May 31, 2026</strong>. After that date, no one will be able to log in without it. <strong>Don't wait until the last day</strong> — activate it now, it takes less than 2 minutes.
        </vs-alert>
        <p class="text-sm mb-2"><strong>Choose your second factor:</strong></p>
        <ul class="text-sm mb-4" style="list-style: disc; padding-left: 1.25rem;">
          <li><strong>Authenticator app (strongly recommended)</strong> — Google Authenticator, Microsoft Authenticator, Authy, 1Password. Works offline, no email delays, much harder to phish.</li>
          <li><strong>Email code</strong> — a 6-digit code sent to your inbox at every sign-in. Slower, and only as safe as your mailbox.</li>
        </ul>
        <div class="flex justify-end mt-4">
          <vs-button type="border" class="mr-2" @click="remindLater">Remind me later</vs-button>
          <vs-button color="danger" icon-pack="feather" icon="icon-shield" @click="step = 'steps'">Protect my account now</vs-button>
        </div>
      </div>

      <vs-alert v-if="hardBlock && step !== 'prompt'" color="danger" icon-pack="feather" icon="icon-shield-off" class="mb-4" :active="true">
        Two-factor authentication is now mandatory. You cannot continue using XYLO without activating it.
      </vs-alert>

      <!-- STEPS 1-3 — choose method → verify → recovery codes. Shared with the
           forced full-page flow (/pages/2fa-setup) via TwoFASetupSteps. -->
      <TwoFASetupSteps v-if="step === 'steps'" @done="finish" />
    </div>
  </vs-popup>
</template>

<script>
import twofa from '@/http/requests/auth/twofa.js'
import TwoFASetupSteps from '@/components/twofa/TwoFASetupSteps.vue'

// [DEBUG] verify the module is imported at all
// eslint-disable-next-line no-console
console.log('[2FA-modal] module loaded')

// After this date the popup becomes non-dismissable (hard block). Until then
// the user can pick "Remind me later" and continue using the app.
// UTC end-of-day on the deadline so the user has the whole day available.
const TWOFA_DEADLINE = new Date('2026-05-31T23:59:59Z')

export default {
  name: 'MandatoryTwoFAModal',
  components: { TwoFASetupSteps },
  // skipPrompt=true skips the soft-warning step and lands directly on
  // method-choice. Used when opened from ProfilSecurity ("Enable 2FA"),
  // where the user has already committed and the warning is redundant.
  props: {
    skipPrompt: { type: Boolean, default: false }
  },
  created () {
    // [DEBUG] verify component is created (remove once stable)
    // eslint-disable-next-line no-console
    console.log('[2FA-modal] created')
  },
  data () {
    return {
      // prompt | steps
      // 'prompt' is the soft-warning shown before the deadline; 'steps' hands
      // over to TwoFASetupSteps (choose / verify / recovery).
      step: this.skipPrompt ? 'steps' : 'prompt',
      // Local override: once the user clicks "Remind me later" we hide the
      // modal until next page load. Vue doesn't observe localStorage, so we
      // need a reactive flag here.
      sessionDismissed: false,
      // Reactive mirrors of localStorage state. We init from localStorage at
      // mount and update them on login/logout/activation. Reading localStorage
      // directly inside computed properties would not trigger reactivity.
      mustEnable: !!localStorage.getItem('xylo:must-enable-2fa'),
      hasToken: !!localStorage.getItem('accessToken')
    }
  },
  computed: {
    hardBlock () {
      return Date.now() >= TWOFA_DEADLINE.getTime()
    },
    visible: {
      get () { return this.shouldShow },
      // Pre-deadline, vs-popup may try to close on backdrop/escape — let it
      // (state is also flipped via remindLater so this branch is rarely hit).
      // Post-deadline, button-close-hidden=true plus this no-op makes the
      // modal genuinely non-dismissable.
      set (val) {
        if (this.hardBlock) return
        if (!val) this.remindLater()
      }
    },
    shouldShow () {
      // Render only if logged in AND user must enable 2FA AND not currently
      // on the login page (where the user can't have a session anyway).
      const onLogin = (this.$route && this.$route.path && this.$route.path.startsWith('/pages/login'))
      // Pre-deadline: a session dismiss hides the modal until next page load.
      if (!this.hardBlock && this.sessionDismissed) return false
      return this.hasToken && this.mustEnable && !onLogin
    }
  },
  methods: {
    remindLater () {
      // Soft dismiss: clear the flag for this session. Server will set it
      // again on the next login until the user actually activates 2FA.
      localStorage.removeItem('xylo:must-enable-2fa')
      this.mustEnable = false
      this.sessionDismissed = true
      this.step = 'prompt'
    },
    finish () {
      localStorage.removeItem('xylo:must-enable-2fa')
      // Reload so router-guards / cached status pick up the new state cleanly
      // (ProfilSecurity polls this flag to close its popup).
      window.location.reload()
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler () {
        // [DEBUG] verify watcher fires (remove once verified)
        // eslint-disable-next-line no-console
        console.log('[2FA-modal] watch.$route fired, path=', this.$route && this.$route.path)
        this.hasToken   = !!localStorage.getItem('accessToken')
        this.mustEnable = !!localStorage.getItem('xylo:must-enable-2fa')
        const onLogin = this.$route && this.$route.path && this.$route.path.startsWith('/pages/login')
        // eslint-disable-next-line no-console
        console.log('[2FA-modal] state', { hasToken: this.hasToken, mustEnable: this.mustEnable, onLogin })
        if (!this.hasToken || onLogin) return
        twofa.status()
          .then(res => {
            // eslint-disable-next-line no-console
            console.log('[2FA-modal] /2fa/status →', res && res.data)
            if (!res || !res.data) return
            if (res.data.twofa_enabled === false) {
              localStorage.setItem('xylo:must-enable-2fa', '1')
              this.mustEnable = true
              this.sessionDismissed = false
            } else if (res.data.twofa_enabled === true) {
              localStorage.removeItem('xylo:must-enable-2fa')
              this.mustEnable = false
            }
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error('[2FA-modal] /2fa/status FAIL:', err && err.message)
          })
      }
    }
  }
}
</script>
